/**
 * ACCORDION BEHAVIOR
 * ---------------------------------------------------------------------------------------------------------------------
 * Reemplaza las animaciones de apertura/cierre de <accordion-disclosure> (snippet 'accordion'). Las originales dejaban
 * el acordeón en un estado inconsistente si se hacía clic durante la animación (height en línea congelado con
 * overflow:hidden, o el atributo "open" eliminado justo después de reabrir), por lo que el bloque parecía "no abrir"
 * hasta un clic extra.
 *
 * La regla es que "aria-expanded" es la única fuente de verdad: la animación es decorativa y, pase lo que pase (clic a
 * mitad de animación, animación congelada en una pestaña en segundo plano), el finalizador deja el elemento
 * exactamente en el estado que indica ese atributo.
 *
 * Vive en un asset y no en un <script> inline porque se carga en todas las páginas del sitio: así el navegador lo
 * cachea una vez en lugar de descargarlo con cada documento. El CSS que lo acompaña está al final de theme.css.
 */

/* Sin Web Animations API dejamos intacta la implementación del tema */
if ('animate' in Element.prototype) {
  const DURATION = 250;
  const SAFETY_DELAY = DURATION + 200;

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

  const clearStyles = (details) => {
    details.style.height = '';
    details.style.overflow = '';
  };

  /**
   * Cierra la animación en curso (si la hay) y deja el <details> en el estado que describe aria-expanded.
   * Es idempotente y se invoca desde tres sitios: al terminar la animación, desde un temporizador de seguridad (por si
   * la animación nunca llega a ejecutarse) y al inicio de cada open/close.
   */
  const finalize = (host) => {
    const record = host.__accordionAnimation;

    if (!record) return;

    host.__accordionAnimation = null;
    clearTimeout(record.timer);
    record.animation.onfinish = null;

    try {
      record.animation.cancel();
    } catch (error) {
      /* ya no estaba activa */
    }

    record.details.open = record.details.getAttribute('aria-expanded') === 'true';
    clearStyles(record.details);
  };

  /**
   * pinFinalHeight fija por adelantado la altura final en línea. Se usa al cerrar para que, si la animación se
   * interrumpe o nunca corre, el estado visible siga siendo "cerrado" y no un salto a la altura completa.
   */
  const animateHeight = (host, details, from, to, pinFinalHeight) => {
    details.style.overflow = 'hidden';

    if (pinFinalHeight) details.style.height = `${to}px`;

    const animation = details.animate(
      [{ height: `${from}px` }, { height: `${to}px` }],
      { duration: DURATION, easing: 'ease' }
    );

    host.__accordionAnimation = {
      animation,
      details,
      timer: setTimeout(() => finalize(host), SAFETY_DELAY)
    };

    animation.onfinish = () => finalize(host);
  };

  customElements.whenDefined('accordion-disclosure').then(() => {
    const AccordionDisclosure = customElements.get('accordion-disclosure');

    if (!AccordionDisclosure) return;

    AccordionDisclosure.prototype.open = function (options) {
      const details = this.disclosureElement;
      const summary = this.summaryElement;

      if (!details || !summary) return;

      finalize(this);

      details.open = true;
      details.setAttribute('aria-expanded', 'true');
      clearStyles(details);

      if (options?.instant || reducedMotion.matches) return;

      const from = summary.clientHeight;
      const to = details.scrollHeight;

      if (to > from) animateHeight(this, details, from, to, false);
    };

    AccordionDisclosure.prototype.close = function () {
      const details = this.disclosureElement;
      const summary = this.summaryElement;

      if (!details || !summary) return;

      finalize(this);
      details.setAttribute('aria-expanded', 'false');

      if (!details.open) return;

      const from = details.clientHeight;
      const to = summary.clientHeight;

      if (reducedMotion.matches || from <= to) {
        details.open = false;
        clearStyles(details);
        return;
      }

      animateHeight(this, details, from, to, true);
    };
  });
}
